import { Injectable } from '@angular/core';
import {
  Filters,
  SortBy,
} from 'src/app/components/lista-tabellare/classes/lista-tabellare-data-source';
import {
  Colonna,
  Riga,
} from 'src/app/components/tabella-cfg-dinamica/tabella-cfg-dinamica.component';
import { ESRS } from '../ESRS/esrs.service';
import { KPI } from '../KPI/kpi.service';
import { AzioneBP } from '../azione-e-buona-pratica/azione-e-buona-pratica.service';
import { Certificazione } from '../certificazioni/certificazioni.service';
import { ImpattoIro, Opportunita, Rischio } from '../doppia-materialita/doppia-materialita.service';
import { Impatto } from '../impatto/impatto.service';
import { Impegno } from '../impegni/impegni.service';
import { SottoSettore } from '../settori/settori.service';
import { STDR } from '../standard-rendicontazione/standard-rendicontazione.service';
import { TemaIro } from '../tema/tema.service';
import { ValoriKpi } from './../KPI/kpi.service';
import { TematicheWebService } from './tematiche-web.service';

export interface SDGS {
  chiave: string;
  valore: string;
}

export type ModalitaTematica = 'DUE_DILIGENCE' | 'QUESTIONARIO';

/**
 * True se la tematica è in modalità Questionario. Un valore assente (tematiche salvate prima
 * dell'introduzione del campo, template già pubblicati) vale DUE_DILIGENCE.
 */
export function isTematicaQuestionario(
  tematica: { modalita?: ModalitaTematica | null } | null | undefined,
): boolean {
  return (tematica?.modalita ?? 'DUE_DILIGENCE') === 'QUESTIONARIO';
}

export interface TematicaBase {
  id: string;
  chiave: string;
  obbligatorio: boolean;
  domanda: string;
  descrizione: string;
  note: string;
  idKpis: string[];
  tipo: string;
  punteggio: 0;
  punteggioPercentuale: 0;
  opzioni: OpzioneTematica[];
  risposteVsme: OpzioneTematica[];
  risposte: string[] | null;
  commento: string;
  min: number;
  max: number;
  risposta?: Risposta;
  sottodomande?: string[];
  chiaveGruppoUM: string;
  stdr?: string[];
  sdgs?: SDGS[];
  esrs?: ESRS[];
  sottoSettori?: SottoSettore[];
  aree: string[];
  nomeBreve: string;
  idsAmbiti?: string[];
  locked?: boolean;
  preSelectedInfos?: string[];
  minLivelloRispostaAutomatica?: number;
  /** Meccanismo delle risposte. Se assente vale DUE_DILIGENCE. */
  modalita?: ModalitaTematica | null;
  /** Solo QUESTIONARIO: true = una o più risposte, false/assente = una sola risposta. */
  multirisposta?: boolean;
  infoConsigliatoObbl?: {
    consigliatoModuliAssociati: string[];
    stdrConsigliati?: STDR[];
    stdrObbligatori?: STDR[];
  };
  consigliato?: boolean;
  materiale?: boolean;
  elementi?: any;
  info?: string[];
  idAzienda?: string;
}

export interface Tematica extends TematicaBase {
  impatti: Impatto[];
  impegni?: Impegno[];
  kpis?: KPI[];
  azioni?: AzioneBP[];
  certificazioni?: Certificazione[];
}
export interface TematicaSurvey extends TematicaBase {
  impattiDtos: Impatto[];
}
export interface TematicaDoppiaSurvey extends TematicaBase {
  temiIro: TemaIro[];
  impattiIro: ImpattoIro[];
  rischi: Rischio[];
  opportunita: Opportunita[];
}

export interface OpzioneTematica {
  id?: string;
  chiave: string;
  tipo: 'checkbox' | 'azione' | 'kpi' | 'dynamic_table';
  valore: string;
  note: string;
  punteggio: string;
  sottoOpzioni: OpzioneTematica[];
  sottoRisposte?: string[];
  value?: number; // valore della sottoRisposta ( serve pre calcolo del punteggio)
  descrizione: string;
  idCategoriaUnitaDiMisura: string;
  idUnitaDiRiferimento: string;
  valoreAttribuito: number;
  risposta: string;
  checked: boolean;
  valoriKpi: ValoriKpi[];
  valoriTarget?: ValoriKpi[];
  nomeBreve: string;
  titolo: string;
  colonne: Colonna[];
  righe: Riga[];
  totaleRighe: boolean;
  totaleColonne: boolean;
  titoloTabella: string;
  codiceNormaVsme?: string;
  descrizioneNormaVsme?: string;
  moduloCompleto?: boolean;
  preCompilazioneInfos?: string;
  notRisposta?: boolean;
  /** Solo QUESTIONARIO, sulle risposte: true = una o più sottorisposte, false/assente = una sola. */
  multirisposta?: boolean;
  infoNonDisponibile?: {
    checked: false;
    risposta: string;
  };
  infoNonRilevante?: {
    checked: false;
    risposta: string;
  };
}
export interface AreeTematica {
  chiave: string;
  descrizione: string;
}

export interface Risposta {
  id: string;
  chiave: string;
  valore: string;
  um: string;
}

@Injectable({
  providedIn: 'root',
})
export class TematicheService {
  constructor(private readonly tematicheWebService: TematicheWebService) {}
  /**
   * Funzione per selezionare le righe da far vedere nella grid
   * @param page pagina
   * @param pageSize record per pagina
   * @param ricerca risultato per ricerca
   * @param filters valori da filtrare
   * @param sortBy sort asc desc
   * @returns lista di righe
   */
  public getTematiche(
    page: number,
    pageSize: number,
    ricerca?: string,
    filters?: Filters[],
    sortBy?: SortBy[],
  ) {
    return this.tematicheWebService.get(page, pageSize, ricerca, filters, sortBy);
  }

  /**
   * Funzine ws per la creazione del Tematica
   * @param data dati da inserire nel MongoDB
   * @returns chiama la funzione post Tematiche per la creazione Tematica
   */
  public postTematiche(data: any) {
    return this.tematicheWebService.post(data);
  }

  /**
   * Funzione per eliminare una riga da una lista
   * @param idRiga id della riga da eliminare
   * @returns
   */
  public deleteTematica(idRiga: any) {
    return this.tematicheWebService.delete(idRiga);
  }

  /**
   * Tematica
   * @param id
   * @param tematica
   * @returns
   */
  public putTematica(id: string, tematica: any) {
    return this.tematicheWebService.put(id, tematica);
  }

  public getAree() {
    return this.tematicheWebService.getAree();
  }
  public getStdr(idTematica: string | null, isCreazione: boolean) {
    return this.tematicheWebService.getStdr(idTematica, isCreazione);
  }

  /**
   * Get degli Obiettivi di Sviluppo Sostenibile
   * @returns
   */
  public getSDGS() {
    return this.tematicheWebService.getSDGS();
  }

  /**
   * Put degli Obiettivi di Sviluppo Sostenibile
   * @returns
   */
  public putSDGS(idTematica: string) {
    return this.tematicheWebService.putSDGS(idTematica);
  }

  public exportTabVsme(idTematica: string) {
    return this.tematicheWebService.exportTabVsme(idTematica);
  }
}
